/**
 * <lp-marquee> — Endlosschleife für die USP-Leiste.
 *
 * Der Kern des Problems: eine nahtlose Schleife braucht mindestens zwei
 * identische Durchläufe hintereinander. Ist ein Durchlauf schmaler als der
 * Viewport, reichen zwei nicht — es klafft eine Lücke. Der naive Ausweg ist,
 * den Satz fest 5–6× zu duplizieren; auf Desktop steht dann sichtbar derselbe
 * Text mehrfach nebeneinander.
 *
 * Deshalb wird hier gemessen statt geraten:
 *   1. Breite eines Durchlaufs ermitteln.
 *   2. n = wie oft passt er in den Viewport (aufgerundet, mindestens 1).
 *      Das ist die "Einheit" — die kleinste Menge, die die Breite sicher füllt.
 *   3. Die Einheit genau einmal klonen und um exakt eine Einheitsbreite
 *      verschieben. Bei jedem Durchlauf sieht das Layout identisch aus.
 *
 * Auf Desktop sorgt das CSS (große Schrift, weite Gaps, Trennzeichen) dafür,
 * dass n meist 1 oder 2 ist — die Wiederholung liegt damit außerhalb bzw. am
 * Rand des Sichtfelds statt fünffach in der Mitte.
 *
 * Die Geschwindigkeit ist in px/s angegeben, nicht als feste Dauer: sonst
 * liefe eine Leiste mit acht Einträgen doppelt so schnell wie eine mit vier.
 *
 * Zugänglichkeit:
 *   - Klone sind aria-hidden und aus dem Tab-Fluss genommen; Screenreader
 *     lesen die Liste genau einmal.
 *   - prefers-reduced-motion: keine Animation, keine Klone. Die Leiste bleibt
 *     die horizontal wischbare Zeile, die sie ohne JS auch ist.
 *   - Pause bei Hover und bei Tastaturfokus innerhalb der Leiste.
 */
class LpMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-marquee-track]');
    this.set = this.querySelector('[data-marquee-set]');
    if (!this.track || !this.set) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = () => this.build();
    this.reduceMotion.addEventListener('change', this.onMotionChange);

    // Erst ab spürbarer Änderung neu aufbauen: die Adressleiste mobiler
    // Browser löst beim Scrollen sonst dauernd Resize-Events aus.
    this.lastWidth = this.offsetWidth;
    this.maybeRebuild = () => {
      const w = this.offsetWidth;
      if (Math.abs(w - this.lastWidth) < 40) return;
      this.lastWidth = w;
      this.build();
    };

    // Zwei Quellen, absichtlich doppelt: ResizeObserver erwischt auch
    // Breitenänderungen ohne Fensterresize (Theme-Editor-Panel, Zoom), feuert
    // aber nur, wenn der Renderer läuft. window.resize ist der verlässliche
    // Fallback. Die 40px-Schwelle macht doppeltes Auslösen folgenlos.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.maybeRebuild());
      this.observer.observe(this);
    }

    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.maybeRebuild, 150);
    };
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('orientationchange', this.onResize, { passive: true });

    // Webfonts ändern die Textbreite nachträglich — nach dem Laden neu messen.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.build());
    }

    this.build();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.reduceMotion?.removeEventListener('change', this.onMotionChange);
    clearTimeout(this.resizeTimer);
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
    }
  }

  clearClones() {
    this.track.querySelectorAll('[data-marquee-clone]').forEach((el) => el.remove());
  }

  build() {
    if (!this.track || !this.set) return;

    this.clearClones();
    this.track.style.removeProperty('--lp-marquee-distance');
    this.track.style.removeProperty('animation-duration');
    this.classList.remove('is-animating');

    if (this.reduceMotion.matches) return;

    const viewport = this.offsetWidth;
    const setWidth = this.set.scrollWidth;
    if (!viewport || !setWidth) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const stride = setWidth + gap;

    // Wie viele Durchläufe füllen den Viewport mindestens einmal komplett?
    const perUnit = Math.max(1, Math.ceil(viewport / stride));
    const unitWidth = perUnit * stride;

    // Insgesamt zwei Einheiten: das Original plus (2 × perUnit − 1) Klone.
    const clonesNeeded = perUnit * 2 - 1;
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < clonesNeeded; i++) {
      const clone = this.set.cloneNode(true);
      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('role');
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      fragment.appendChild(clone);
    }
    this.track.appendChild(fragment);

    const speed = Math.max(10, parseFloat(this.dataset.speed) || 50);
    this.track.style.setProperty('--lp-marquee-distance', unitWidth + 'px');
    this.track.style.animationDuration = unitWidth / speed + 's';
    this.classList.add('is-animating');
  }
}

if (!customElements.get('lp-marquee')) {
  customElements.define('lp-marquee', LpMarquee);
}
